import { useEffect, useState } from "react";
import { useL } from "@/lib/useL";
import { getConsent, setConsent } from "@/lib/analytics";

/**
 * Minimal RGPD-compliant cookie banner (CNIL 2020 guideline).
 * - Shows only if the user hasn't picked yet.
 * - Explicit Accept / Refuse buttons at same visual weight.
 * - No pre-checked box, no dark pattern.
 * - Persists choice in localStorage; can be reset from /privacy page.
 */
export const CookieBanner = () => {
  const L = useL();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      if (!getConsent()) setVisible(true);
    }, 800);
    return () => clearTimeout(t);
  }, []);

  const decide = (value) => {
    setConsent(value);
    setVisible(false);
  };

  // Pupil pages (quiz, cards, mind map) stay clean: no banner over the answer buttons.
  if (!visible || /^\/(q|c|m)\//.test(window.location.pathname)) return null;

  return (
    <div
      data-testid="cookie-banner"
      className="fixed bottom-0 left-0 right-0 z-[60] p-3 sm:p-4 bg-background border-t-2 border-[#002FA7] shadow-2xl"
      role="dialog"
      aria-label={L("Consentement aux cookies", "Cookie consent")}
    >
      <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6">
        <div className="flex-1 text-xs sm:text-sm leading-relaxed">
          <p className="font-mono uppercase tracking-[0.15em] text-[10px] sm:text-[11px] mb-1 text-muted-foreground">
            {L("Cookies & mesure d'audience", "Cookies & audience measurement")}
          </p>
          <p>
            {L("SUX1 utilise Google Analytics pour comprendre comment améliorer l'app. Aucune donnée personnelle n'est partagée. Vous pouvez refuser sans impact sur votre expérience.", "SUX1 uses Google Analytics to understand how to improve the app. No personal data is shared. You can decline with no impact on your experience.")}{" "}
            <a href="/privacy" className="underline hover:no-underline">
              {L("En savoir plus", "Learn more")}
            </a>
            .
          </p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
          <button
            onClick={() => decide("denied")}
            data-testid="cookie-refuse-btn"
            className="flex-1 sm:flex-none font-mono uppercase tracking-[0.2em] text-[10px] sm:text-[11px] px-3 sm:px-4 py-2 border border-border hover:bg-[#002FA7] hover:text-white transition-colors"
          >
            {L("Refuser", "Decline")}
          </button>
          <button
            onClick={() => decide("granted")}
            data-testid="cookie-accept-btn"
            className="flex-1 sm:flex-none font-mono uppercase tracking-[0.2em] text-[10px] sm:text-[11px] bg-[#002FA7] text-white px-3 sm:px-4 py-2 hover:bg-[#00227A] transition-colors"
          >
            {L("Accepter", "Accept")}
          </button>
        </div>
      </div>
    </div>
  );
};
